import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  fetchBillingConfig,
  registerRevolutWebhook,
  runBillingJobs,
  syncRevolutSubscriptionPlans,
  updateBillingConfig,
} from "@/lib/platform-api";
import PlatformPage from "@/components/platform/PlatformPage";

export default function PlatformBillingSettings() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [webhookBusy, setWebhookBusy] = useState(false);
  const [plansBusy, setPlansBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await fetchBillingConfig();
      setConfig(data.config);
      setForm(data.config || {});
    } catch (err) {
      toast.error(err.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await updateBillingConfig(form);
      setConfig(data.config);
      setForm(data.config);
      toast.success("Billing settings saved");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const runJobs = async () => {
    try {
      const data = await runBillingJobs();
      toast.success("Billing jobs completed", {
        description: `Renewals: ${data.renewalInvoicesCreated}, overdue: ${data.markedOverdue}, suspended: ${data.suspended}`,
      });
    } catch (err) {
      toast.error(err.message);
    }
  };

  const registerWebhook = async () => {
    setWebhookBusy(true);
    try {
      const data = await registerRevolutWebhook();
      setConfig(data.config);
      setForm(data.config || {});
      toast.success("Revolut webhook registered", {
        description: `${data.action === "created" ? "Created" : "Updated"} ${data.url}`,
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setWebhookBusy(false);
    }
  };

  const syncPlans = async () => {
    setPlansBusy(true);
    try {
      await syncRevolutSubscriptionPlans();
      toast.success("Subscription plans synced with Revolut");
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPlansBusy(false);
    }
  };

  const webhookUrl = config?.webhookCallbackUrl || "https://aivoryems.com/api/webhooks/revolut";

  return (
    <PlatformPage
      title="Revolut & billing settings"
      description="Store production API keys here. Secret keys are masked after save."
      maxWidth="max-w-[720px]"
      headerExtra={
        <Link to="/platform/billing" className="text-xs text-[var(--text-tertiary)] hover:text-[var(--primary)] shrink-0">
          ← Billing queue
        </Link>
      }
    >
      <div className="surface p-6 w-full">
        <div className="text-overline mb-2">Revolut webhook</div>
        <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
          Revolut Merchant webhooks are registered via API (there is no dashboard button). Click below after your secret
          key is saved — we register this URL with Revolut and store the signing secret automatically.
        </p>
        <code className="mt-3 block font-mono text-xs text-[var(--text-primary)] break-all">{webhookUrl}</code>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button type="button" className="btn-secondary text-sm" onClick={syncPlans} disabled={plansBusy}>
            {plansBusy ? "Syncing…" : "Sync subscription plans"}
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={registerWebhook}
            disabled={webhookBusy || !config?.hasSecretKey}
          >
            {webhookBusy ? "Registering…" : "Register webhook with Revolut"}
          </button>
          {config?.hasWebhookSecret ? (
            <span className="text-xs font-mono text-[var(--primary)]">Signing secret saved</span>
          ) : (
            <span className="text-xs text-[var(--text-tertiary)]">Not registered yet</span>
          )}
        </div>
      </div>

      <form onSubmit={save} className="mt-8 space-y-4 surface p-6">
        <Field label="Revolut secret key (sk_…)" hint={config?.hasSecretKey ? "Saved — leave blank to keep" : "Required for checkout"}>
          <input
            className="input-dark w-full font-mono text-xs"
            placeholder={config?.hasSecretKey ? "••••••••" : "sk_…"}
            value={form.revolutSecretKey || ""}
            onChange={(e) => setForm((p) => ({ ...p, revolutSecretKey: e.target.value }))}
          />
        </Field>
        <Field label="Revolut public key (pk_…)">
          <input
            className="input-dark w-full font-mono text-xs"
            value={form.revolutPublicKey || ""}
            onChange={(e) => setForm((p) => ({ ...p, revolutPublicKey: e.target.value }))}
          />
        </Field>
        <Field label="Webhook signing secret" hint={config?.hasWebhookSecret ? "Saved automatically when you register the webhook above" : "Use Register webhook with Revolut, or paste manually"}>
          <input
            className="input-dark w-full font-mono text-xs"
            placeholder="wsk_…"
            value={form.revolutWebhookSecret || ""}
            onChange={(e) => setForm((p) => ({ ...p, revolutWebhookSecret: e.target.value }))}
          />
        </Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Billing currency">
            <select
              className="input-dark w-full"
              value={form.billingCurrency || "USD"}
              onChange={(e) => setForm((p) => ({ ...p, billingCurrency: e.target.value }))}
            >
              <option value="USD">USD</option>
              <option value="SGD">SGD (converted from USD prices)</option>
            </select>
          </Field>
          <Field label="USD → SGD rate">
            <input
              type="number"
              step="0.01"
              className="input-dark w-full"
              value={form.usdToSgdRate ?? 1.35}
              onChange={(e) => setForm((p) => ({ ...p, usdToSgdRate: Number(e.target.value) }))}
            />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={Boolean(form.revolutSandbox)}
            onChange={(e) => setForm((p) => ({ ...p, revolutSandbox: e.target.checked }))}
          />
          Use Revolut sandbox API
        </label>
        <button type="submit" className="btn-primary text-sm" disabled={busy}>
          {busy ? "Saving…" : "Save settings"}
        </button>
      </form>

      <div className="surface p-6 w-full">
        <div className="text-overline mb-2">Renewal & dunning jobs</div>
        <p className="text-xs text-[var(--text-tertiary)] mb-4">Option B: issue renewal invoices, mark overdue, suspend accounts.</p>
        <button type="button" className="btn-secondary text-sm" onClick={runJobs}>
          Run billing jobs now
        </button>
      </div>
    </PlatformPage>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs text-[var(--text-tertiary)]">{label}</span>
      {hint ? <span className="block text-[10px] text-[var(--text-tertiary)] mt-0.5">{hint}</span> : null}
      <div className="mt-1">{children}</div>
    </label>
  );
}
